import React, { useMemo } from 'react';

import { Skeleton } from '@mui/material';
import { useTranslation } from 'react-i18next';

import EmptyCard from '../../../../../../shared/components/v3/Cards/EmptyCard/EmptyCard';
import ErrorCard from '../../../../../../shared/components/v3/Cards/ErrorCard/ErrorCard';
import { FLEXIBLE_REMUNERATION_BENEFITS } from '../../../../../../shared/constants/flexibleRemuneration';
import { IMemberFRCardBalances } from '../../../../../../shared/interfaces/collaborators';
import { TRANSLATION_COLLABORATORS } from '../../../../../../shared/translations/collaborators';
import { TRANSLATIONS_GLOBAL } from '../../../../../../shared/translations/commons';
import { FLEXIBLE_REMUNERATION_BENEFIT_SKU } from '../../../../../../shared/types/flexibleRemuneration';
import { toLocaleNumberFormat } from '../../../../../../shared/utils/utils';

import './AccumulatedCardBalance.scss';

interface IBalanceTile {
  label: string;
  amount: string;
  highlighted?: boolean;
}

const BalanceTile = ({ label, amount, highlighted = false }: IBalanceTile) => (
  <div className={`balance-tile flex flex-col ${highlighted ? 'highlighted' : ''}`} data-testid="balance-tile">
    <span className="label">{label}</span>
    <span className="amount">{amount}</span>
  </div>
);

interface IAccumulatedCardBalance {
  balances?: IMemberFRCardBalances;
  loading: boolean;
  error: boolean;
  onRetry: () => void;
}

const AccumulatedCardBalance = ({ balances, loading, error, onRetry }: IAccumulatedCardBalance) => {
  const { t } = useTranslation();
  const { balanceCard } = TRANSLATION_COLLABORATORS.detail.collaboratorBalanceRF;

  const cards = useMemo(() => (balances?.balances ?? []).filter(({ balance }) => balance > 0), [balances]);

  const formatBalance = (amount: number) =>
    toLocaleNumberFormat(
      amount,
      {
        style: 'currency',
        currency: balances?.currency,
        useGrouping: true,
        maximumFractionDigits: 2
      },
      'es-ES'
    );

  const getBenefitName = (sku: string) => {
    const benefit = FLEXIBLE_REMUNERATION_BENEFITS[sku as FLEXIBLE_REMUNERATION_BENEFIT_SKU];
    return benefit ? t(benefit.title) : sku;
  };

  const renderContent = () => {
    if (loading) {
      return <Skeleton className="accumulated-card-balance-skeleton" variant="rectangular" height={120} />;
    }

    if (error) {
      return (
        <ErrorCard
          primaryText={t(TRANSLATIONS_GLOBAL.errorCard.title)}
          secondaryText={t(TRANSLATIONS_GLOBAL.errorCard.description)}
          buttonText={t(TRANSLATIONS_GLOBAL.errorCard.buttonText)}
          action={onRetry}
        />
      );
    }

    if (!balances || cards.length === 0) {
      return (
        <EmptyCard
          primaryText={t(balanceCard.emptyState.title)}
          secondaryText={t(balanceCard.emptyState.description)}
        />
      );
    }

    return (
      <div className="balances flex flex-row flex-wrap">
        {cards.map(({ sku, balance }) => (
          <BalanceTile key={sku} label={getBenefitName(sku)} amount={formatBalance(balance)} />
        ))}
        <BalanceTile label={t(balanceCard.total)} amount={formatBalance(balances.total)} highlighted />
      </div>
    );
  };

  return (
    <div className="accumulated-card-balance flex flex-col w-full" data-testid="accumulated-card-balance">
      <div className="top flex flex-row justify-between w-full">
        <div className="title">{t(balanceCard.title)}</div>
      </div>
      {renderContent()}
    </div>
  );
};

export default AccumulatedCardBalance;
